import { type FormEvent, useCallback, useEffect, useState } from "react";
import type { Locale } from "./i18n";

type Copy = (key: string, values?: Record<string, string | number>) => string;

export interface MerchantActivityItem {
  id: string;
  action: "earn" | "redeem" | "adjust" | "expire" | "reverse";
  status: "committed" | "reversed" | "voided";
  source: "staff_scan" | "dynamic_qr" | "pos" | "receipt_claim" | "system";
  verificationLevel:
    "merchant_confirmed" | "pos_confirmed" | "user_claimed" | "system";
  occurredAt: string;
  customer: { displayMemberCode: string };
  location: {
    id: string;
    displayName: string;
    timezone: string;
  };
  actor: {
    userId: string | null;
    displayName: string | null;
    deviceId: string | null;
    deviceLabel: string | null;
  };
  ledgerEffects: Array<{
    type:
      | "earn"
      | "bonus"
      | "threshold_conversion"
      | "redeem"
      | "adjustment"
      | "expiry"
      | "reversal";
    delta: number;
    reasonCode: string;
  }>;
  reversesTransactionId: string | null;
  reversedByTransactionId: string | null;
  requestId: string | null;
}

export interface MerchantActivityPage {
  items: MerchantActivityItem[];
  nextCursor: string | null;
}

type ActivityState =
  | { status: "loading" }
  | { status: "error"; permission: boolean }
  | { status: "ready"; page: MerchantActivityPage };

interface ReversalAttempt {
  transactionId: string;
  reasonCode: string;
  idempotencyKey: string;
}

type ReversalState =
  | { status: "idle"; selectedId: string | null }
  | { status: "submitting"; attempt: ReversalAttempt }
  | {
      status: "error";
      attempt: ReversalAttempt;
      reason:
        "offline" | "forbidden" | "conflict" | "newer-earns" | "unexpected";
    }
  | { status: "complete"; transactionId: string };

function isActivityPage(value: unknown): value is MerchantActivityPage {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const page = value as Record<string, unknown>;
  return (
    Array.isArray(page.items) &&
    (typeof page.nextCursor === "string" || page.nextCursor === null)
  );
}

async function problemCode(response: Response): Promise<string> {
  const value: unknown = await response.json().catch(() => null);
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>).error &&
    typeof (value as Record<string, unknown>).error === "object" &&
    typeof ((value as Record<string, unknown>).error as Record<string, unknown>)
      .code === "string"
  ) {
    return ((value as Record<string, unknown>).error as { code: string }).code;
  }
  return "UNEXPECTED_RESPONSE";
}

export function formatActivityTime(
  item: MerchantActivityItem,
  locale: Locale,
): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: item.location.timezone,
  }).format(new Date(item.occurredAt));
}

export function activityEffectValue(item: MerchantActivityItem): number | null {
  if (item.action === "redeem") return null;
  if (item.action === "earn") {
    return item.ledgerEffects
      .filter((effect) => effect.type === "earn" || effect.type === "bonus")
      .reduce((sum, effect) => sum + Math.max(0, effect.delta), 0);
  }
  return item.ledgerEffects.reduce((sum, effect) => sum + effect.delta, 0);
}

function actionKey(item: MerchantActivityItem): string {
  return `merchant.activity.action.${item.action}`;
}

function effectiveStatusKey(item: MerchantActivityItem): string {
  if (item.reversedByTransactionId) return "merchant.activity.status.reversed";
  return `merchant.activity.status.${item.status}`;
}

function canReverseItem(item: MerchantActivityItem): boolean {
  return (
    (item.action === "earn" || item.action === "redeem") &&
    item.status === "committed" &&
    !item.reversedByTransactionId
  );
}

export function Activity({
  merchantId,
  canReverse,
  locale,
  copy,
  initialPage,
}: {
  merchantId: string;
  canReverse: boolean;
  locale: Locale;
  copy: Copy;
  initialPage?: MerchantActivityPage;
}) {
  const [state, setState] = useState<ActivityState>(() =>
    initialPage
      ? { status: "ready", page: initialPage }
      : { status: "loading" },
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [reasonCode, setReasonCode] = useState("cashier_mistake");
  const [reversal, setReversal] = useState<ReversalState>({
    status: "idle",
    selectedId: null,
  });

  const load = useCallback(
    async (cursor?: string) => {
      if (cursor) setLoadingMore(true);
      else setState({ status: "loading" });
      try {
        const query = new URLSearchParams({ limit: "20" });
        if (cursor) query.set("cursor", cursor);
        const response = await fetch(
          `/v1/merchant/transactions?${query.toString()}`,
          {
            credentials: "include",
            headers: {
              "accept-language": locale,
              "x-merchant-id": merchantId,
            },
          },
        );
        if (!response.ok) {
          setState({
            status: "error",
            permission: response.status === 401 || response.status === 403,
          });
          return;
        }
        const value: unknown = await response.json();
        if (!isActivityPage(value)) {
          setState({ status: "error", permission: false });
          return;
        }
        setState((current) => ({
          status: "ready",
          page:
            cursor && current.status === "ready"
              ? {
                  items: [...current.page.items, ...value.items],
                  nextCursor: value.nextCursor,
                }
              : value,
        }));
      } catch {
        setState({ status: "error", permission: false });
      } finally {
        setLoadingMore(false);
      }
    },
    [locale, merchantId],
  );

  useEffect(() => {
    if (initialPage) return;
    void load();
  }, [initialPage, load]);

  const submitReversal = useCallback(
    async (attempt: ReversalAttempt) => {
      setReversal({ status: "submitting", attempt });
      try {
        const response = await fetch(
          `/v1/merchant/transactions/${encodeURIComponent(
            attempt.transactionId,
          )}/reversals`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "accept-language": locale,
              "content-type": "application/json",
              "idempotency-key": attempt.idempotencyKey,
            },
            body: JSON.stringify({ reasonCode: attempt.reasonCode }),
          },
        );
        if (!response.ok) {
          const code = await problemCode(response);
          setReversal({
            status: "error",
            attempt,
            reason:
              code === "REVERSAL_NEWER_EARNS_EXIST"
                ? "newer-earns"
                : response.status === 403
                  ? "forbidden"
                  : response.status === 409 ||
                      code === "TRANSACTION_NOT_REVERSIBLE"
                    ? "conflict"
                    : "unexpected",
          });
          return;
        }
        setReversal({
          status: "complete",
          transactionId: attempt.transactionId,
        });
        if (!initialPage) await load();
      } catch {
        setReversal({ status: "error", attempt, reason: "offline" });
      }
    },
    [initialPage, load, locale],
  );

  const startReversal = (event: FormEvent, transactionId: string) => {
    event.preventDefault();
    void submitReversal({
      transactionId,
      reasonCode,
      idempotencyKey: `idem_reverse_ui_${crypto
        .randomUUID()
        .replaceAll("-", "")}`,
    });
  };

  if (state.status === "loading") {
    return (
      <section className="panel activity-panel" aria-busy="true">
        <div className="loading-mark" aria-hidden="true" />
        <h2>{copy("merchant.activity.loading")}</h2>
      </section>
    );
  }
  if (state.status === "error") {
    return (
      <section className="panel empty-state" role="alert">
        <span className="empty-mark" aria-hidden="true" />
        <h2>
          {copy(
            state.permission
              ? "merchant.activity.permissionTitle"
              : "merchant.activity.loadErrorTitle",
          )}
        </h2>
        <p>
          {copy(
            state.permission
              ? "merchant.activity.permissionBody"
              : "merchant.activity.loadErrorBody",
          )}
        </p>
        {!state.permission && (
          <button
            className="primary-button"
            type="button"
            onClick={() => void load()}
          >
            {copy("common.retry")}
          </button>
        )}
      </section>
    );
  }

  return (
    <section className="panel activity-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{copy("merchant.nav.transactions")}</p>
          <h2>{copy("merchant.activity.title")}</h2>
        </div>
        <span className="activity-count">
          {copy("merchant.activity.count", {
            count: new Intl.NumberFormat(locale).format(
              state.page.items.length,
            ),
          })}
        </span>
      </div>
      {reversal.status === "complete" && (
        <p className="activity-feedback" role="status">
          {copy("merchant.activity.reversalComplete")}
        </p>
      )}
      {state.page.items.length === 0 ? (
        <div className="empty-state compact-empty">
          <span className="empty-mark" aria-hidden="true" />
          <h2>{copy("merchant.activity.emptyTitle")}</h2>
          <p>{copy("merchant.activity.emptyBody")}</p>
        </div>
      ) : (
        <div className="activity-list">
          {state.page.items.map((item) => {
            const selected =
              reversal.status === "idle" && reversal.selectedId === item.id;
            const submitting =
              reversal.status === "submitting" &&
              reversal.attempt.transactionId === item.id;
            const failed =
              reversal.status === "error" &&
              reversal.attempt.transactionId === item.id;
            const effectValue = activityEffectValue(item);
            return (
              <article className="activity-record" key={item.id}>
                <div className="activity-primary">
                  <div className={`activity-icon action-${item.action}`}>
                    <span aria-hidden="true" />
                  </div>
                  <div>
                    <div className="activity-title-line">
                      <strong>{copy(actionKey(item))}</strong>
                      <span>{copy(effectiveStatusKey(item))}</span>
                    </div>
                    <p>
                      {item.customer.displayMemberCode} ·{" "}
                      {item.location.displayName}
                    </p>
                    <time dateTime={item.occurredAt}>
                      {formatActivityTime(item, locale)}
                    </time>
                  </div>
                  <b
                    className={
                      effectValue !== null && effectValue < 0 ? "negative" : ""
                    }
                  >
                    {effectValue === null
                      ? copy("merchant.activity.rewardEffect")
                      : `${effectValue > 0 ? "+" : ""}${new Intl.NumberFormat(
                          locale,
                        ).format(effectValue)}`}
                  </b>
                </div>
                <details>
                  <summary>{copy("merchant.activity.details")}</summary>
                  <dl className="activity-metadata">
                    <div>
                      <dt>{copy("merchant.activity.actor")}</dt>
                      <dd>
                        {item.actor.displayName ??
                          (item.actor.deviceLabel
                            ? copy("merchant.activity.sharedTillActor")
                            : copy("merchant.activity.systemActor"))}
                      </dd>
                    </div>
                    <div>
                      <dt>{copy("merchant.activity.device")}</dt>
                      <dd>
                        {item.actor.deviceLabel ?? copy("common.notAvailable")}
                      </dd>
                    </div>
                    <div>
                      <dt>{copy("merchant.activity.requestId")}</dt>
                      <dd>{item.requestId ?? copy("common.notAvailable")}</dd>
                    </div>
                    {item.reversesTransactionId && (
                      <div>
                        <dt>{copy("merchant.activity.reverses")}</dt>
                        <dd>{item.reversesTransactionId}</dd>
                      </div>
                    )}
                    {item.reversedByTransactionId && (
                      <div>
                        <dt>{copy("merchant.activity.reversedBy")}</dt>
                        <dd>{item.reversedByTransactionId}</dd>
                      </div>
                    )}
                  </dl>
                </details>
                {canReverse && canReverseItem(item) && !selected && !failed && (
                  <button
                    className="text-button reversal-toggle"
                    type="button"
                    onClick={() =>
                      setReversal({ status: "idle", selectedId: item.id })
                    }
                  >
                    {copy("merchant.activity.reverseAction")}
                  </button>
                )}
                {selected && (
                  <form
                    className="reversal-form"
                    onSubmit={(event) => startReversal(event, item.id)}
                  >
                    <label htmlFor={`reversal-reason-${item.id}`}>
                      {copy("merchant.activity.reversalReason")}
                    </label>
                    <select
                      id={`reversal-reason-${item.id}`}
                      value={reasonCode}
                      onChange={(event) => setReasonCode(event.target.value)}
                    >
                      {[
                        "cashier_mistake",
                        "refund",
                        "duplicate",
                        "customer_dispute",
                        "operator_correction",
                      ].map((reason) => (
                        <option value={reason} key={reason}>
                          {copy(`merchant.activity.reason.${reason}`)}
                        </option>
                      ))}
                    </select>
                    <p>{copy("merchant.activity.reversalWarning")}</p>
                    <div className="scanner-actions">
                      <button
                        className="quiet-button"
                        type="button"
                        onClick={() =>
                          setReversal({ status: "idle", selectedId: null })
                        }
                      >
                        {copy("common.cancel")}
                      </button>
                      <button className="primary-button" type="submit">
                        {copy("merchant.activity.confirmReversal")}
                      </button>
                    </div>
                  </form>
                )}
                {submitting && (
                  <p className="activity-feedback" role="status">
                    {copy("merchant.activity.reversing")}
                  </p>
                )}
                {failed && (
                  <div className="activity-feedback error" role="alert">
                    <p>
                      {copy(
                        reversal.reason === "newer-earns"
                          ? "merchant.activity.reversalNewerEarns"
                          : reversal.reason === "forbidden"
                            ? "merchant.activity.reversalForbidden"
                            : reversal.reason === "conflict"
                              ? "merchant.activity.reversalConflict"
                              : reversal.reason === "offline"
                                ? "merchant.activity.reversalOffline"
                                : "merchant.activity.reversalError",
                      )}
                    </p>
                    {(reversal.reason === "offline" ||
                      reversal.reason === "unexpected") && (
                      <button
                        className="quiet-button"
                        type="button"
                        onClick={() => void submitReversal(reversal.attempt)}
                      >
                        {copy("merchant.activity.retryReversal")}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      {state.page.nextCursor && (
        <button
          className="quiet-button activity-more"
          type="button"
          disabled={loadingMore}
          onClick={() => void load(state.page.nextCursor ?? undefined)}
        >
          {copy(
            loadingMore
              ? "merchant.activity.loadingMore"
              : "merchant.activity.loadMore",
          )}
        </button>
      )}
    </section>
  );
}
